import { useSyncExternalStore } from "react";

export type Lang = "th" | "en";

const STORAGE_KEY = "yala:lang";

function readSavedLang(): Lang | null {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === "th" || v === "en" ? v : null;
}

// ── Shared single source of truth (mirrors useTheme) ──────────────────────
// One module-level value read through useSyncExternalStore so every consumer
// (TopBar, panels, KPI strip, map labels) flips in lockstep when the language
// toggle is clicked — no per-component useState copies drifting out of sync.
//
// Thai-FIRST by design: this is a control tower for เทศบาลนครยะลา. The Mayor and
// his staff work in Thai; English is the secondary view available via the toggle.
let currentLang: Lang = readSavedLang() ?? "th";
const listeners = new Set<() => void>();

function applyToDom(lang: Lang): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("lang", lang);
}

applyToDom(currentLang);

function setLangGlobal(l: Lang): void {
  if (l === currentLang) return;
  currentLang = l;
  try {
    window.localStorage.setItem(STORAGE_KEY, l);
  } catch {
    // Safari private mode may throw
  }
  applyToDom(l);
  listeners.forEach((fn) => fn());
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function getSnapshot(): Lang {
  return currentLang;
}

function getServerSnapshot(): Lang {
  return "th";
}

/** Current language + setter/toggle. */
export function useLang(): { lang: Lang; setLang: (l: Lang) => void; toggle: () => void } {
  const lang = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return {
    lang,
    setLang: setLangGlobal,
    toggle: () => setLangGlobal(currentLang === "th" ? "en" : "th"),
  };
}

/**
 * Translation hook. Pass both strings at the call site — no separate dictionary
 * file to drift out of sync with the UI:
 *
 *   const t = useT();
 *   <PanelHeader title={t("CITY PULSE", "ชีพจรเมือง")} />
 *
 * Co-locating en/th keeps each translation next to the component that renders it,
 * which is what makes a few-hours bilingual pass tractable across dozens of files.
 */
export function useT(): (en: string, th: string) => string {
  const { lang } = useLang();
  return (en: string, th: string) => (lang === "th" ? th : en);
}
